import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import {
  StyledCard,
  CardHeaderStyled,
} from 'pages/Orders/OrdersShow/OrdersShowCommonInfo/OrdersShowCommonInfo.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useShowController } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Collapse } from 'shared/mui/Collapse/Collapse';
import { ExpandIconButton } from 'shared/mui/IconButtons';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { Typography } from 'shared/mui/Typography';
import { FunctionField, TextField } from 'shared/react-admin/Fields';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { Gateway } from 'types/embedded/gateway';
import { Method } from 'types/embedded/method';
import { Order } from 'types/embedded/order';

import { OrdersImageFieldStyled } from '../../../components/OrdersImageField';

export const OrdersShowCommonInfoProvider = (): JSX.Element => {
  const translate = useTypedTranslate();

  const { record: order } = useShowController<Order>();
  const { hasResourceAccess } = usePermissions();

  const [isExpanded, setIsExpanded] = useState(true);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <RecordContextProvider value={order}>
      <StyledCard>
        <CardHeaderStyled
          appendActions={
            <ExpandIconButton isExpanded={isExpanded} onClick={handleExpandClick} size="small" />
          }
          sx={{ paddingBottom: '16px' }}
          title={translate('app.orders.providerDetails')}
          titleSize="smallBold"
        />
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ padding: '0 16px 16px !important' }}>
            <CardTable tableBodyProps={{ sx: { '& td': { width: '107px' } } }}>
              <TableRowWrapper label={translate('app.table.provider')}>
                <ReferenceField link={false} reference="gateways" source="gateway.@id">
                  <FunctionField
                    render={(record: Gateway) => {
                      return (
                        <RecordContextProvider value={record}>
                          <ReferenceField link={false} reference="providers" source="provider.@id">
                            <TextField source="name" />
                          </ReferenceField>
                        </RecordContextProvider>
                      );
                    }}
                    source="provider"
                  />
                </ReferenceField>
              </TableRowWrapper>
              <TableRowWrapper label={translate('app.table.method')}>
                <ReferenceField
                  emptyText={EMPTY_FIELD}
                  link={false}
                  reference="methods"
                  source="method.@id"
                >
                  <FunctionField
                    render={(record: Method) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {hasResourceAccess(Resources.MethodLogos) && record?.logo && (
                          <OrdersImageFieldStyled source="logo.filePath" />
                        )}
                        <Typography variant="body2">{record?.name}</Typography>
                      </Box>
                    )}
                  />
                </ReferenceField>
              </TableRowWrapper>
              <TableRowWrapper label={translate('app.table.gateway')}>
                <ReferenceField link={false} reference="gateways" source="gateway.@id">
                  <TextField source="name" />
                </ReferenceField>
              </TableRowWrapper>
            </CardTable>
          </CardContent>
        </Collapse>
      </StyledCard>
    </RecordContextProvider>
  );
};
